import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { Box, CircularProgress, Tab, Tabs, ThemeProvider, useMediaQuery, useTheme } from '@mui/material';
import { DataGridPro, GridToolbar } from '@mui/x-data-grid-pro';
import { useFarmData } from '../context/FarmDataContext';
import CropTimeline from './CropTimeline';
import theme from '../theme.js';

const CropsPage = () => {
    const { farm_id } = useParams();
    const location = useLocation();
    const idToken = location.state?.idToken;
    const { farmData, fetchFarmData, loading } = useFarmData();
    const [tabValue, setTabValue] = useState(0);
    const [selectedRow, setSelectedRow] = useState(null);
    const [selectedRowId, setSelectedRowId] = useState(null);
    const [cropEvents, setCropEvents] = useState([]);
    const muiTheme = useTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));

    // Get location filters from URL state
    const section_id = location.state?.section_id;
    const plot_id = location.state?.plot_id;
    const crop_id = location.state?.crop_id;

    // Handle row click
    const handleRowClick = (params) => {
        if (selectedRow && selectedRow.id === params.row.id) {
            setSelectedRow(null); // Deselect row
            setSelectedRowId(null); // Clear selected row ID
        } else {
            setSelectedRow(params.row); // Select row
            setSelectedRowId(params.row.id); // Set selected row ID

        }
    };

    // Handle clicking outside the table
    const handleDeselect = (event) => {
        if (!event.target.closest('.MuiDataGrid-root')) {
            setSelectedRow(null); // Deselect row
            setSelectedRowId(null); // Clear selected row ID
        }
    };

    useEffect(() => {
        if (idToken && !farmData) {
            fetchFarmData(farm_id, idToken);
        }
    }, [farm_id, idToken, farmData, fetchFarmData]);

    const rows = useMemo(() => {
        if (!farmData?.crops || !farmData?.harvests || !farmData?.sales) return [];

        // Filter crops based on location
        let filteredCrops = farmData.crops;
        
        if (section_id) {
            // Filter by section/zone - crops have zone_id that directly references the section
            filteredCrops = filteredCrops.filter(crop => crop.zone_id === section_id);
        }
        
        if (plot_id) {
            // Filter by specific plot
            filteredCrops = filteredCrops.filter(crop => crop.plot_id === plot_id);
        }
        
        // Note: crop_id filtering is disabled for this page as requested

        // Sort crops by last harvest date (most recent first)
        const sortedCrops = filteredCrops.sort((a, b) => {
            const aHarvests = farmData.harvests.filter((harvest) => harvest.crop_id === a.id);
            const bHarvests = farmData.harvests.filter((harvest) => harvest.crop_id === b.id);
            
            const aLastHarvestDate = aHarvests.reduce((latest, h) => {
                const date = new Date(h.date);
                return date > latest ? date : latest;
            }, new Date(a.planting_date));
            
            const bLastHarvestDate = bHarvests.reduce((latest, h) => {
                const date = new Date(h.date);
                return date > latest ? date : latest;
            }, new Date(b.planting_date));
            
            // Sort by most recent first (descending order)
            return bLastHarvestDate - aLastHarvestDate;
        });

        return sortedCrops.map((crop) => {
            const cropHarvests = farmData.harvests.filter((harvest) => harvest.crop_id === crop.id);

            const totalYield = cropHarvests.reduce((sum, h) => sum + (h.harvest_amt || 0), 0);
            const totalSales = farmData.sales
                .filter((sale) => cropHarvests.some((h) => h.id === sale.harvest_id))
                .reduce((sum, sale) => sum + (sale.sale_amt || 0), 0);

            const lastHarvestDate = cropHarvests.reduce((latest, h) => {
                const date = new Date(h.date);
                return date > latest ? date : latest;
            }, new Date(crop.planting_date));

            const actualYield = crop.plot_area
                ? (totalYield / crop.plot_area).toFixed(2)
                : 0;

            const yieldPerPlant = crop.units_planted
                ? (totalYield / crop.units_planted).toFixed(2)
                : 0;

            const duration = crop.transplant_date
                ? Math.ceil(
                    (new Date(lastHarvestDate) - new Date(crop.transplant_date)) /
                    (1000 * 60 * 60 * 24)
                )
                : null;

            return {
                id: crop.id,
                variety_name: crop.variety_name,
                plant_type: crop.plant_type,
                planting_date: crop.planting_date,
                transplant_date: crop.transplant_date,
                last_harvest_date: lastHarvestDate.toISOString().split('T')[0],
                units_planted: crop.units_planted,
                zone_name: crop.zone_name || '',
                plot_name: crop.plot_name || '',
                plot_area: crop.plot_area || 0,
                plot_area_unit: crop.plot_area_unit || '',
                target_yield: crop.target_yield || 0,
                target_yield_unit_type: crop.target_unit_type || '',
                total_yield: totalYield,
                total_sales: totalSales,
                actual_yield: actualYield,
                yield_per_plant: yieldPerPlant,
                duration,
            };
        });
    }, [farmData, section_id, plot_id]);

    const columns = [
        { field: 'variety_name', headerName: 'Variety Name', flex: 1, minWidth: 120 },
        { field: 'plant_type', headerName: 'Plant Type', flex: 1, minWidth: 120 },
        { field: 'planting_date', headerName: 'Planting Date', flex: 1, minWidth: 120 },
        { field: 'transplant_date', headerName: 'Transplant Date', flex: 1, minWidth: 120 },
        { field: 'last_harvest_date', headerName: 'Last Harvest Date', flex: 1, minWidth: 120 },
        { field: 'duration', headerName: 'Duration (Days)', flex: 1, minWidth: 120 },
        { field: 'units_planted', headerName: 'Units Planted', flex: 1, minWidth: 120 },
        { field: 'zone_name', headerName: 'Section Name', flex: 1, minWidth: 120 },
        { field: 'plot_name', headerName: 'Plot Name', flex: 1, minWidth: 120 },
        { field: 'plot_area', headerName: 'Plot Area', flex: 1, minWidth: 120 },
        { field: 'plot_area_unit', headerName: 'Plot Area Unit', flex: 1, minWidth: 120 },
        { field: 'total_yield', headerName: 'Total Yield', flex: 1, minWidth: 120 },
        { field: 'total_sales', headerName: 'Total Sales', flex: 1, minWidth: 120 },
        { field: 'target_yield', headerName: 'Target Yield', flex: 1, minWidth: 120 },
        { field: 'target_yield_unit_type', headerName: 'Target Yield Unit', flex: 1, minWidth: 120 },
        { field: 'actual_yield', headerName: 'Actual Yield', flex: 1, minWidth: 120 },
        { field: 'yield_per_plant', headerName: 'Yield Per Plant', flex: 1, minWidth: 120 },
    ];

    const handleTabChange = (event, newValue) => {
        setTabValue(newValue);
    };

    const getDetailPanelContent = useCallback(
        ({ row }) => {
            const cropId = row.id;

            // Retrieve crop-specific data from farmData
            const crop = farmData.crops.find((c) => c.id === cropId);
            if (!crop) {
                return (
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                        <CircularProgress />
                    </Box>
                );
            }

            // Use last_harvest_date directly from the row
            const lastHarvestDate = new Date(row.last_harvest_date);

            // Common filter function for events
            const filterEventsByDate = (events) =>
                events.filter(
                    (event) =>
                        event.plot_id === crop.plot_id &&
                        new Date(event.date) >= new Date(crop.transplant_date) &&
                        new Date(event.date) <= lastHarvestDate
                );

            // Only fetch and set events if they haven't been cached already
            if (!cropEvents[cropId]) {
                const fertilizerEvents = filterEventsByDate(farmData.fertilizerEvents || []);
                const fungicideEvents = filterEventsByDate(farmData.fungicideEvents || []);
                const insecticideEvents = filterEventsByDate(farmData.insecticideEvents || []);
                const herbicideEvents = filterEventsByDate(farmData.herbicideEvents || []);
                const harvests = farmData.harvests.filter((h) => h.crop_id === cropId);
                const sales = farmData.sales.filter((sale) =>
                    harvests.some((harvest) => harvest.id === sale.harvest_id)
                );

                setCropEvents((prevEvents) => ({
                    ...prevEvents,
                    [cropId]: {
                        fertilizerEvents,
                        fungicideEvents,
                        insecticideEvents,
                        herbicideEvents,
                        harvests,
                        sales,
                    },
                }));
            }

            const eventData = cropEvents[cropId] || {};

            const eventColumns = (nameField) => [
                { field: 'id', headerName: 'ID', flex: 1, minWidth: 80 },
                { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
                { field: nameField, headerName: 'Product Name', flex: 1, minWidth: 150 },
                { field: 'amt_used', headerName: 'Amount Used', flex: 1, minWidth: 120 },
                { field: 'amt_used_unit_type', headerName: 'Unit Type', flex: 1, minWidth: 100 },
                { field: 'event_cost', headerName: 'Event Cost', flex: 1, minWidth: 120 },
            ];

            const harvestColumns = [
                { field: 'id', headerName: 'Harvest ID', flex: 1, minWidth: 100 },
                { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
                { field: 'harvest_amt', headerName: 'Harvest Amount', flex: 1, minWidth: 120 },
                { field: 'harvest_unit_type', headerName: 'Unit Type', flex: 1, minWidth: 100 },
            ];

            const salesColumns = [
                { field: 'id', headerName: 'Sale ID', flex: 1, minWidth: 100 },
                { field: 'date', headerName: 'Date', flex: 1, minWidth: 120 },
                { field: 'sale_amt', headerName: 'Sale Amount', flex: 1, minWidth: 120 },
                { field: 'unit_price', headerName: 'Unit Price', flex: 1, minWidth: 120 },
            ];

            const tabContent = [
                <DataGridPro
                    rows={eventData.fertilizerEvents || []}
                    columns={eventColumns('fertilizer_name')}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
                <DataGridPro
                    rows={eventData.fungicideEvents || []}
                    columns={eventColumns('fungicide_name')}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
                <DataGridPro
                    rows={eventData.insecticideEvents || []}
                    columns={eventColumns('insecticide_name')}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
                <DataGridPro
                    rows={eventData.herbicideEvents || []}
                    columns={eventColumns('herbicide_name')}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
                <DataGridPro
                    rows={eventData.harvests || []}
                    columns={harvestColumns}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
                <DataGridPro
                    rows={eventData.sales || []}
                    columns={salesColumns}
                    autoHeight
                    pageSize={10}
                    pagination
                    slots={{ toolbar: GridToolbar }}
                    slotProps={{
                        toolbar: {
                            showQuickFilter: true,
                            quickFilterProps: { debounceMs: 500 },
                        },
                    }}
                    sx={{
                        bgcolor: 'transparent',
                        color: '#fff',
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': {
                            bgcolor: '#222C2F !important',
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                            borderBottom: '1px solid rgba(255,255,255,0.12)',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            bgcolor: '#222C2F !important',
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            color: '#fff',
                            fontWeight: 500,
                            fontSize: 13,
                        },
                        '& .MuiDataGrid-cell': {
                            bgcolor: 'transparent',
                            color: '#fff',
                            fontSize: 12,
                            borderBottom: '1px solid rgba(255,255,255,0.08)',
                            py: 0.5,
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:nth-of-type(odd)': {
                            bgcolor: 'rgba(255,255,255,0.02)',
                        },
                        '& .MuiDataGrid-row:nth-of-type(even)': {
                            bgcolor: 'transparent',
                        },
                        '& .MuiDataGrid-row:hover': {
                            bgcolor: 'rgba(255,255,255,0.04)',
                        },
                        '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                            color: '#F9AA33',
                        },
                    }}
                />,
            ];

            return (
                <Box sx={{ 
                    p: { xs: 2, md: 3 }, 
                    bgcolor: '#181F2A', 
                    overflow: 'auto',
                    borderRadius: 1.5,
                    boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)',
                    m: { xs: 1, md: 2 }
                }}>
                    <Tabs 
                        value={tabValue} 
                        onChange={handleTabChange} 
                        textColor="primary" 
                        indicatorColor="primary"
                        sx={{
                            mb: 2,
                            '& .MuiTab-root': {
                                color: '#AAB4C8',
                                fontSize: { xs: 12, md: 14 },
                                fontWeight: 500,
                                minWidth: { xs: 80, md: 120 },
                                '&.Mui-selected': {
                                    color: '#F9AA33',
                                },
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#F9AA33',
                            },
                        }}
                    >
                        <Tab label="Fertilizer" />
                        <Tab label="Fungicide" />
                        <Tab label="Insecticide" />
                        <Tab label="Herbicide" />
                        <Tab label="Harvests" />
                        <Tab label="Sales" />
                    </Tabs>
                    <Box sx={{ mt: 2 }}>{tabContent[tabValue]}</Box>
                </Box>
            );
        },
        [farmData, cropEvents, tabValue, handleTabChange, theme]
    );

    const getDetailPanelHeight = useCallback(() => 400, []);

    if (loading || !farmData) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <ThemeProvider theme={theme}>
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: "100%",
                    height: '95vh', // Full viewport height
                    bgcolor: '#181F2A',
                    transition: 'all 0.3s ease-in-out',
                }}
                onClick={handleDeselect} // Handle deselect when clicking outside
            >
                {/* Container Section (70% Height when a row is selected) */}

                {selectedRow && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', width: '100%', bgcolor: '#181F2A', p: 0, m: 0, overflow: 'hidden' }}>
                        <Box sx={{ flex: 1, minHeight: 0, width: '100%', p: 0, m: 0 }}>
                            <CropTimeline
                                units_planted={selectedRow.units_planted}
                                startDate={selectedRow.transplant_date}
                                endDate={selectedRow.last_harvest_date}
                                latitude={farmData.farmDetails.lat || 0}
                                longitude={farmData.farmDetails.lon || 0}
                                events={cropEvents[selectedRow.id] || []}
                                cropDetails={selectedRow}
                                onBack={() => setSelectedRow(null)}
                            />
                        </Box>
                    </Box>
                )}

                {!selectedRow && (
                    <Box
                        sx={{
                            flex: 1,
                            transition: 'flex 0.3s ease-in-out',
                            overflow: 'auto',
                            p: { xs: 0, md: 3 },
                            mt: { xs: 0, md: -1 },
                            mb: { xs: 8, md: 0 },
                        }}
                    >
                        <Box sx={{ 
                            backgroundColor: '#181F2A', 
                            borderRadius: 1.5, 
                            boxShadow: '0 2px 8px 0 rgba(0,0,0,0.20)', 
                            p: { xs: '0px 8px 16px 0px', md: '8px 24px 24px 24px' },
                            height: { xs: 'calc(100vh - 130px)', md: 'calc(100vh - 26px)' },
                            overflow: 'hidden',
                        }}>
                        <DataGridPro
                            disableCellSelectionOnClick
                            onRowClick={handleRowClick}
                            rows={rows}
                            columns={columns}
                            pageSize={10}
                            rowsPerPageOptions={[10, 20, 50]}
                            getDetailPanelContent={getDetailPanelContent}
                            getDetailPanelHeight={getDetailPanelHeight}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 500 },
                                },
                            }}
                            pagination
                            selectionModel={selectedRowId ? [selectedRowId] : []} // Explicitly manage row selection
                            sx={{
                                    height: '95%',
                                    bgcolor: 'transparent',
                                    color: '#fff',
                                    border: 'none',
                                    '& .MuiDataGrid-columnHeaders': {
                                        bgcolor: '#222C2F !important',
                                        color: '#fff',
                                        fontWeight: 500,
                                        fontSize: { xs: 12, md: 13 },
                                        borderBottom: '1px solid rgba(255,255,255,0.12)',
                                    },
                                    '& .MuiDataGrid-columnHeader': {
                                        bgcolor: '#222C2F !important',
                                    },
                                    '& .MuiDataGrid-columnHeaderTitle': {
                                        color: '#fff',
                                        fontWeight: 500,
                                        fontSize: { xs: 12, md: 13 },
                                    },
                                    '& .MuiDataGrid-cell': {
                                        bgcolor: 'transparent',
                                        color: '#fff',
                                        fontSize: { xs: 11, md: 12 },
                                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                                        py: 0.5,
                                    },
                                    '& .MuiDataGrid-virtualScroller': {
                                        bgcolor: 'transparent',
                                    },
                                    '& .MuiDataGrid-row': {
                                        height: { xs: 40, md: 48 },
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(odd)': {
                                        bgcolor: 'rgba(255,255,255,0.02)',
                                    },
                                    '& .MuiDataGrid-row:nth-of-type(even)': {
                                        bgcolor: 'transparent',
                                    },
                                    '& .MuiDataGrid-row:hover': {
                                        bgcolor: 'rgba(255,255,255,0.04)',
                                    },
                                    '& .MuiDataGrid-toolbarContainer button, & .MuiDataGrid-toolbarContainer .MuiTypography-root': {
                                        color: '#F9AA33',
                                    },
                                    '& .MuiDataGrid-footerContainer': {
                                        bgcolor: '#222C2F',
                                        color: '#fff',
                                        borderTop: '1px solid rgba(255,255,255,0.12)',
                                    },
                            }}
                        />
                        </Box>
                    </Box>
                )}
            </Box>
        </ThemeProvider>
    );
};

export default CropsPage;
